/**
 * イメージボードの手描きストロークの幾何計算。
 *
 * 描画そのもの（React / SVG）から切り離して、パス生成・間引き・当たり判定だけを
 * 純関数で持つ。テストしやすくするためと、描画中に毎フレーム走る処理なので
 * 副作用を持たせないため。
 *
 * 座標はすべてボードのワールド座標（画像アイテムと同じ平面）。
 */
import type { StrokePoint } from "../../app/services/imageBoard";

/** 筆圧非対応のポインタ（マウス・指）で使う既定値。 */
export const DEFAULT_PRESSURE = 0.5;

/**
 * 筆圧による太さの変調幅。
 * pressure 0 → 太さ 40%、pressure 1 → 太さ 160% になる。
 * 0 にしてしまうと線が消えるため下限を残す。
 */
const PRESSURE_MIN_SCALE = 0.4;
const PRESSURE_MAX_SCALE = 1.6;

/** 筆圧から実際の線幅を求める。 */
export const widthForPressure = (baseWidth: number, pressure: number): number => {
  const clamped = Math.min(1, Math.max(0, pressure));
  return (
    baseWidth * (PRESSURE_MIN_SCALE + clamped * (PRESSURE_MAX_SCALE - PRESSURE_MIN_SCALE))
  );
};

/**
 * 描画中に溜めた点を間引く。
 *
 * ポインタイベントは 1 秒に数百回来るので、そのまま保存すると 1 ストロークが
 * 数千点になり、転送量も再描画コストも無駄に膨らむ。直前の点から
 * `minDistance`（ワールド単位）動いていない点は落とす。始点と終点は必ず残す。
 */
export const simplifyStroke = (
  points: StrokePoint[],
  minDistance: number,
): StrokePoint[] => {
  if (points.length <= 2) return points;

  const result: StrokePoint[] = [points[0]];
  const threshold = minDistance * minDistance;
  for (let i = 1; i < points.length - 1; i += 1) {
    const last = result[result.length - 1];
    const dx = points[i][0] - last[0];
    const dy = points[i][1] - last[1];
    if (dx * dx + dy * dy >= threshold) result.push(points[i]);
  }
  result.push(points[points.length - 1]);
  return result;
};

/**
 * 点列を滑らかな SVG パスへ変換する。
 *
 * 各点をそのまま直線で結ぶとポインタのサンプリング間隔で角張るため、
 * 隣り合う点の中点を通る二次ベジエでつなぐ（実装が軽く、遅延も出ない）。
 */
export const toSvgPath = (points: StrokePoint[]): string => {
  if (points.length === 0) return "";
  if (points.length === 1) {
    const [x, y] = points[0];
    // 点を打っただけのときも見えるように、極小の線分にする
    return `M ${x} ${y} L ${x + 0.01} ${y}`;
  }

  let d = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 1; i < points.length - 1; i += 1) {
    const [cx, cy] = points[i];
    const midX = (cx + points[i + 1][0]) / 2;
    const midY = (cy + points[i + 1][1]) / 2;
    d += ` Q ${cx} ${cy} ${midX} ${midY}`;
  }
  const last = points[points.length - 1];
  d += ` L ${last[0]} ${last[1]}`;
  return d;
};

/** 太さの揃った 1 区間ぶんの SVG パス。 */
export interface StrokeBand {
  d: string;
  width: number;
}

/**
 * 筆圧の変化を太さの違う区間に分けて描くための分割。
 *
 * SVG の `stroke-width` は 1 パスで 1 値しか持てないので、ストローク全体を
 * 1 本のパスにすると筆圧が表現できない。かといって線分ごとにパスを作ると
 * DOM が膨らむため、太さが `tolerance`（相対値）を超えて変わったところだけで
 * 区間を切る。区間の境目は点を共有するので隙間はできず、丸いキャップと
 * 相まって継ぎ目は見えない。
 */
export const toWidthBands = (
  points: StrokePoint[],
  baseWidth: number,
  tolerance = 0.15,
): StrokeBand[] => {
  if (points.length === 0) return [];
  if (points.length === 1) {
    return [
      {
        d: toSvgPath(points),
        width: widthForPressure(baseWidth, points[0][2]),
      },
    ];
  }

  const bands: StrokeBand[] = [];
  let bandStart = 0;
  let bandWidth = widthForPressure(baseWidth, points[0][2]);

  const pushBand = (endIndex: number, width: number) => {
    const slice = points.slice(bandStart, endIndex + 1);
    if (slice.length >= 2) bands.push({ d: toSvgPath(slice), width });
  };

  for (let i = 1; i < points.length; i += 1) {
    const width = widthForPressure(baseWidth, points[i][2]);
    const changed = Math.abs(width - bandWidth) / bandWidth > tolerance;
    if (changed) {
      pushBand(i, (bandWidth + width) / 2);
      // 次の区間は今の点から始める（端点を共有して隙間を作らない）
      bandStart = i;
      bandWidth = width;
    }
  }
  pushBand(points.length - 1, bandWidth);
  return bands;
};

/** 軸平行の外接矩形。当たり判定の粗いふるい分けに使う。 */
export interface StrokeBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export const strokeBounds = (points: StrokePoint[]): StrokeBounds => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of points) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
};

/** 線分 ab と点 p の距離の 2 乗。 */
const distanceSqToSegment = (
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number => {
  const abx = bx - ax;
  const aby = by - ay;
  const lengthSq = abx * abx + aby * aby;
  // 始点と終点が同じ（点）なら点同士の距離
  const t =
    lengthSq === 0
      ? 0
      : Math.min(1, Math.max(0, ((px - ax) * abx + (py - ay) * aby) / lengthSq));
  const dx = px - (ax + t * abx);
  const dy = py - (ay + t * aby);
  return dx * dx + dy * dy;
};

/**
 * 消しゴムの円（中心 + 半径）がストロークに触れているか。
 *
 * まず外接矩形で弾いてから、線分ごとの距離を見る。ストローク数が増えても
 * 大半は矩形判定で落ちるので、なぞりながらの判定でも十分軽い。
 */
export const isStrokeHit = (
  points: StrokePoint[],
  x: number,
  y: number,
  radius: number,
): boolean => {
  if (points.length === 0) return false;

  const bounds = strokeBounds(points);
  if (
    x < bounds.minX - radius ||
    x > bounds.maxX + radius ||
    y < bounds.minY - radius ||
    y > bounds.maxY + radius
  ) {
    return false;
  }

  const radiusSq = radius * radius;
  if (points.length === 1) {
    const dx = x - points[0][0];
    const dy = y - points[0][1];
    return dx * dx + dy * dy <= radiusSq;
  }

  for (let i = 0; i < points.length - 1; i += 1) {
    const hit = distanceSqToSegment(
      x, y, points[i][0], points[i][1], points[i + 1][0], points[i + 1][1],
    );
    if (hit <= radiusSq) return true;
  }
  return false;
};
